import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Card } from '@core/models/card';
import { ToastService } from '@core/services/toast.service';
import { CourseStatus } from '@core/enums/course-status';

@Component({
  selector: 'prism-card-list',
  templateUrl: './card-list.component.html',
  styleUrl: './card-list.component.scss'
})
export class CardListComponent {
  @Input() dataList: Card[] = [];
  @Input() listName: string = 'default';
  @Input() listLimit: number = 0;
  @Input() isPublic: boolean = false;
  @Output() cardClick = new EventEmitter<Card>();
  CourseStatus = CourseStatus;

  private toastService = inject(ToastService);

  blobImgUrl: SafeResourceUrl = '';

  constructor(private sanitizer: DomSanitizer) {}

  handleClick(card: Card) {
    this.cardClick.emit(card);
  }

  view(card: Card) {
    if (card?.id) {
      this.cardClick.emit(card);
    } else {
      this.toastService.error('Error: Unable to view');
    }
  }

  trackByCardId(index: number, card: Card): number {
    return card.id ?? index;
  }

  /**
   * Builds one accessible label for a focusable card. role="button" + aria-label
   * makes the label the entire accessible name and suppresses descendant content
   * (status banner, image alt, title, description) from being announced — so every
   * piece a sighted user sees must be composed here, in reading order.
   * Image description follows the visual rule: only an author-provided imageAlt is
   * announced; the decorative "Cover image for ..." fallback is left out.
   */
  getCardAriaLabel(card: Card): string {
    const parts: string[] = [];

    const status = card?.status;
    if (
      status === CourseStatus.Completed ||
      status === CourseStatus.Reviewed ||
      status === CourseStatus.Incomplete
    ) {
      parts.push(`Course status: ${status}`);
    }

    // Visible text first, in on-screen reading order (status, title, summary),
    // so it stays a contiguous substring of the accessible name — required by
    // WCAG 2.5.3 "Label in Name". The image description is NOT visible text, so
    // it goes last; placing it between the title and summary would break the
    // contiguity of the visible label and fail the check.
    if (card?.title) parts.push(card.title);

    const description = card?.summary || card?.description;
    if (description) parts.push(description);

    if (card?.imageAlt) parts.push(card.imageAlt);

    return parts.join('. ');
  }

  generateUrlWithSasToken(sasToken: string, blobUrl?: string) {
    if (blobUrl && sasToken) {
      const urlWithSasToken = `${blobUrl}${sasToken}`;
      return this.sanitizer.bypassSecurityTrustResourceUrl(urlWithSasToken);
    }

    return ""
  }
}
